import { useState, useEffect, useRef } from "react";
import {
  Circle,
  GoogleMap,
  Marker,
  useLoadScript,
} from "@react-google-maps/api";
import { CommonText } from "styledComponents/common";

const containerStyle = {
  width: "100%",
  height: "280px",
  borderRadius: "12px",
  overflow: "hidden",
};

const mapOptions = {
  streetViewControl: false,
  fullscreenControl: false,
  mapTypeControl: false,
  disableDoubleClickZoom: true,
  gestureHandling: "cooperative",
};

const circleOptions = {
  strokeColor: "#6F7AFF",
  strokeWeight: 2,
  fillColor: "#6F7AFF",
  fillOpacity: 0.2,
};

// Resolve API key at module load time (build time) to avoid runtime process access
const GOOGLE_MAPS_API_KEY = 
  process.env.REACT_APP_GOOGLE_MAPS_JAVASCRIPT_API_KEY ||
  process.env.REACT_APP_GOOGLE_MAPS_KEY ||
  "";

const SingleLocationMap = ({ location }) => {
  const [mapReady, setMapReady] = useState(false);
  const [mapInstance, setMapInstance] = useState(null);
  const prevLocationRef = useRef(null);

  // Call useLoadScript at the top level (before any conditional returns)
  // Skip loading if no API key is available
  const { isLoaded, loadError } = useLoadScript({
    id: "single-location-map-loader",
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    preventGoogleFontsLoading: true,
  });

  // Reset map ready state when location changes
  useEffect(() => {
    const locationKey = location ? `${location.latitude}-${location.longitude}-${location.radius}` : null;
    if (locationKey !== prevLocationRef.current) {
      setMapReady(false);
      prevLocationRef.current = locationKey;
    }
  }, [location]);

  // Early return if no location
  if (!location) {
    return (
      <CommonText margin="0 0 10px 0" fontSize="13px" color="#5a5f7d">
        Select a location to preview it on the map.
      </CommonText>
    );
  }

  // Validate and parse coordinates early
  const lat = Number(location.latitude);
  const lng = Number(location.longitude);

  // Comprehensive validation before attempting to load anything
  if (
    location.latitude === undefined ||
    location.longitude === undefined ||
    location.latitude === null ||
    location.longitude === null ||
    location.latitude === "" ||
    location.longitude === "" ||
    isNaN(lat) ||
    isNaN(lng) ||
    !isFinite(lat) ||
    !isFinite(lng) ||
    lat < -90 ||
    lat > 90 ||
    lng < -180 ||
    lng > 180
  ) {
    return (
      <CommonText margin="0 0 10px 0" fontSize="13px" color="#5a5f7d">
        Select a location to preview it on the map.
      </CommonText>
    );
  }

  // Map loading states
  if (loadError) {
    return (
      <CommonText margin="0 0 10px 0" fontSize="13px" color="#fe5969">
        Map cannot be loaded. Please check your API key configuration.
      </CommonText>
    );
  }

  if (!isLoaded) {
    return (
      <CommonText margin="0 0 10px 0" fontSize="13px" color="#5a5f7d">
        Loading map...
      </CommonText>
    );
  }

  // Coordinates are already validated above, so we can use them safely
  const center = { lat, lng };
  const radius = Number(location.radius) || 0;

  try {
    return (
      <GoogleMap
        key={`map-${lat}-${lng}`}
        mapContainerStyle={containerStyle}
        center={center}
        zoom={15}
        options={mapOptions}
        onLoad={(map) => {
          setMapInstance(map);
          setMapReady(true);
        }}
      >
        <Marker position={center} />
        {/* Render circle without OverlayView to avoid cross-origin issues */}
        {mapReady && radius > 0 && (
          <Circle center={center} radius={radius} options={circleOptions} />
        )}
      </GoogleMap>
    );
  } catch (error) {
    console.error("Error rendering SingleLocationMap:", error);
    return (
      <CommonText margin="0 0 10px 0" fontSize="13px" color="#fe5969">
        Error loading map. Please try again.
      </CommonText>
    );
  }
};

export default SingleLocationMap;
